import { PageShell } from '@chopchop-se/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router';

import { buildNavSections } from '../app/nav';
import { logout } from '../auth/auth-api';
import { useAuthStore, usePermissionCheck } from '../auth/auth-store';
import { FullPageSpinner } from '../components/FullPageSpinner';
import { AppTopBar } from './AppTopBar';
import { Sidebar } from './Sidebar';

/**
 * The admin chrome: shared glass header (PageShell) + permission-filtered
 * sidebar + routed content.
 *
 * Restaurant users never see this — `homePathFor` sends a portal-only account
 * to `/portal` — but if one navigates here by hand they get a sidebar with only
 * what they may use, and the server refuses the rest anyway.
 */
export function AdminLayout(): ReactNode {
  const { t } = useTranslation(['shell', 'common']);
  const [navOpen, setNavOpen] = useState(false);
  const can = usePermissionCheck();
  const user = useAuthStore((s) => s.user);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const sections = useMemo(() => buildNavSections(can), [can]);
  const showPortalLink = can(['orders.portal.order']);

  function handleSignOut(): void {
    void (async () => {
      try {
        await logout();
      } finally {
        setAnonymous();
        queryClient.clear();
        void navigate('/login', { replace: true });
      }
    })();
  }

  return (
    <PageShell
      header={
        <AppTopBar
          systemLabel={t('shell:layout.adminApp')}
          onToggleNav={() => {
            setNavOpen((open) => !open);
          }}
          crossLink={showPortalLink ? { to: '/portal', label: t('shell:layout.portalApp') } : undefined}
          onSignOut={handleSignOut}
        />
      }
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2 focus:shadow-lifted"
      >
        {t('shell:layout.skipToContent')}
      </a>

      <div className="mx-auto flex w-full max-w-[110rem] gap-6 px-4 pb-12 sm:px-6">
        <Sidebar
          sections={sections}
          open={navOpen}
          onClose={() => {
            setNavOpen(false);
          }}
        />

        <main id="main" className="min-w-0 flex-1 pt-6" aria-label={user?.email ?? ''}>
          <Suspense fallback={<FullPageSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </PageShell>
  );
}
